import React, { Component } from 'react';
import { bool, func, number, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError } from '../../components';
import {
  textEditorValidation,
  composeValidators,
  allowedHtmlTagsValidation,
  richTextLength,
  richTextMaxLength,
  richTextMinLength,
} from '../../util/validators';
import css from './FieldTextEditor.css';
const ReactQuill = typeof window === 'object' ? require('react-quill') : () => false;

const modulesBase = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
  keyboard: {
    bindings: {
      // Disable auto-list creation on "1. " pattern
      'list autofill': {
        key: ' ',
        collapsed: true,
        format: { list: false },
        prefix: /^\d+\.$/,
        handler: function() {
          return true; // Let the space key work normally
        },
      },
    },
  },
};

// The HTML tags Quill emits for each whitelisted format; paragraph structure
// (p, br) is always allowed. Used to validate the stored HTML value against
// the same allowlist the editor variant enforces while editing.
const BASE_ALLOWED_TAGS = ['p', 'br'];
const FORMAT_TO_TAGS = {
  header: ['h2'], // every toolbar variant offers only header level 2
  bold: ['strong'],
  italic: ['em'],
  underline: ['u'],
  strike: ['s'],
  blockquote: ['blockquote'],
  list: ['ol', 'ul', 'li'],
  link: ['a'],
};

// The `formats` whitelist only limits format names, so a pasted <h1>…<h6>
// keeps its original level — normalize every pasted heading to the level 2
// the toolbars offer. Module-scope constant so the modules object stays
// deep-equal across renders (react-quill re-creates the editor when it
// detects a modules change).
const normalizeHeadersMatcher = [
  'h1, h2, h3, h4, h5, h6',
  (node, delta) => {
    delta.ops.forEach(op => {
      if (op.attributes && op.attributes.header) {
        op.attributes.header = 2;
      }
    });
    return delta;
  },
];

// Single source of truth for the toolbar/format allowlist per variant.
// Quill applies `formats` to every Delta write (keyboard, paste, programmatic),
// so disallowed formats are stripped from pasted content in real time.
const getEditorConfig = ({
  isPortfolioWork,
  isApplicationForm,
  isBio,
  isExperienceDescription,
  isJobPostingDescription,
  isCompact,
  withHeading,
  disabled,
}) => {
  let modules;
  let formats;

  if (isPortfolioWork) {
    modules = {
      toolbar: [[{ header: '2' }], ['bold', 'italic'], [{ list: 'ordered' }, { list: 'bullet' }]],
      clipboard: { matchVisual: false, matchers: [normalizeHeadersMatcher] },
    };
    formats = ['header', 'bold', 'italic', 'list'];
  } else if (isJobPostingDescription) {
    modules = {
      toolbar: [[{ header: '2' }], ['bold'], [{ list: 'ordered' }, { list: 'bullet' }]],
      clipboard: { matchVisual: false, matchers: [normalizeHeadersMatcher] },
    };
    formats = ['header', 'bold', 'list'];
  } else if (isApplicationForm) {
    // Application form (ProjectProposalPage) allows links and external info so
    // freelancers can share references with their proposal.
    modules = {
      toolbar: [['bold'], [{ list: 'ordered' }, { list: 'bullet' }]],
      clipboard: { matchVisual: false },
    };
    formats = ['bold', 'list', 'link'];
  } else if (isBio || isExperienceDescription) {
    modules = {
      toolbar: [['bold'], [{ list: 'ordered' }, { list: 'bullet' }]],
      clipboard: { matchVisual: false },
    };
    formats = ['bold', 'list'];
  } else if (isCompact) {
    // Compact toolbar (service descriptions, package descriptions, FAQ
    // answers). The formats stay the default set so legacy markup (lists,
    // blockquotes, strike) isn't stripped by Quill or rejected by the
    // allowed-tags validator. `withHeading` adds an H2 button (the service
    // description); without it a pasted heading becomes a paragraph.
    const toolbar = [['bold', 'italic', 'underline'], [{ list: 'bullet' }]];
    const baseFormats = ['bold', 'italic', 'underline', 'strike', 'blockquote', 'list'];
    modules = withHeading
      ? {
          toolbar: [[{ header: '2' }], ...toolbar],
          clipboard: { matchVisual: false, matchers: [normalizeHeadersMatcher] },
        }
      : { toolbar, clipboard: { matchVisual: false } };
    formats = withHeading ? ['header', ...baseFormats] : baseFormats;
  } else {
    modules = modulesBase;
    formats = ['bold', 'italic', 'underline', 'strike', 'blockquote', 'list'];
  }

  if (disabled) {
    modules = { ...modules, toolbar: false };
  }

  const allowedTags = BASE_ALLOWED_TAGS.concat(
    formats.reduce((tags, format) => tags.concat(FORMAT_TO_TAGS[format] || []), [])
  );

  return { modules, formats, allowedTags };
};

class FieldTextEditorComponent extends Component {
  constructor(props) {
    super(props);
    this.quillRef = null;
    this.setQuillRef = this.setQuillRef.bind(this);
  }

  setQuillRef(el) {
    this.quillRef = el;
    // Chrome's page translation rewrites the text nodes inside the
    // contenteditable editor (wrapping them in <font> tags), which fights
    // Quill's own DOM reconciliation and deletes what the user types in real
    // time. Mark the editable area as not translatable: Chrome honors the
    // translate="no" attribute, the Google Translate widget the
    // "notranslate" class.
    if (el && typeof el.getEditor === 'function') {
      try {
        const editorRoot = el.getEditor().root;
        editorRoot.setAttribute('translate', 'no');
        editorRoot.classList.add('notranslate');
      } catch (e) {
        // Editor not initialized — nothing to mark.
      }
    }
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      rootClassName,
      className,
      inputRootClass,
      quillClass,
      customErrorText,
      id,
      label,
      input,
      meta,
      onUnmount,
      isUncontrolled,
      inputRef,
      placeholder,
      isPortfolioWork,
      isApplicationForm,
      isBio,
      isExperienceDescription,
      isJobPostingDescription,
      isCompact,
      withHeading,
      maxLength,
      maxLengthMessage,
      minLength,
      minLengthMessage,
      disableLinkValidation,
      disabled,
      onFocus,
      onBlur,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */
    if (label && !id) {
      throw new Error('id required when a label is given');
    }

    const { modules, formats } = getEditorConfig({
      isPortfolioWork,
      isApplicationForm,
      isBio,
      isExperienceDescription,
      isJobPostingDescription,
      isCompact,
      withHeading,
      disabled,
    });

    const { valid, invalid, error } = meta;
    const isTextarea = input.type === 'textarea';
    const errorText = customErrorText || error;
    // Show errors as soon as the user has interacted with the field. Quill's
    // blur is unreliable — without `modified`/`dirty` the error would stay
    // hidden while the user types, and the submit button (often disabled on
    // `invalid`) gives no feedback about what went wrong.
    const hasBeenInteractedWith = meta.touched || meta.submitFailed || meta.modified || meta.dirty;
    // A text under the minimum is still being written, so that error waits
    // until the user has left the editor once.
    const isMinLengthErrorOnHold =
      typeof minLength === 'number' &&
      error === minLengthMessage &&
      !(meta.touched || meta.submitFailed);
    const showsError = hasBeenInteractedWith && !isMinLengthErrorOnHold;

    const hasError = !!customErrorText || !!(showsError && invalid && error);

    const fieldMeta = { touched: showsError, error: errorText };

    // Textarea doesn't need type.
    const { type } = input;
    // Uncontrolled input uses defaultValue instead of value.
    const { value: defaultValue, ...inputWithoutValue } = input;
    // Use inputRef if it is passed as prop.
    const refMaybe = inputRef ? { ref: inputRef } : {};

    const quillClassName = classNames(quillClass || css.quill, {
      [css.quillDisabled]: disabled,
    });

    const inputClasses =
      inputRootClass ||
      classNames(css.input, {
        [css.inputSuccess]: valid,
        [css.inputError]: hasError,
        [css.textarea]: isTextarea,
      });

    const inputProps = isUncontrolled
      ? {
          className: inputClasses,
          id,
          type,
          defaultValue,
          ...refMaybe,
          ...inputWithoutValue,
          ...rest,
        }
      : {
          className: inputClasses,
          id,
          type,
          value: input.value,
          onChange: input.onChange,
          ...refMaybe,
          ...input,
          ...rest,
        };

    const hasCounter = typeof maxLength === 'number';
    const classes = classNames(rootClassName || css.root, className);
    const length = hasCounter ? richTextLength(input.value) : 0;
    // The counter is anchored to the editor box (not the field root), so a
    // label above the toolbar doesn't shift it.
    const editorWrapper = quill =>
      hasCounter ? (
        <div className={css.editorWrapper}>
          {quill}
          <span className={classNames(css.counter, { [css.counterOver]: length > maxLength })}>
            {`${length}/${maxLength}`}
          </span>
        </div>
      ) : (
        quill
      );
    if (typeof window !== 'undefined') {
      return (
        <div className={classes}>
          {label ? <label htmlFor={id}>{label}</label> : null}
          <input type="hidden" {...inputProps} style={{ display: 'none' }} />
          {editorWrapper(
            <ReactQuill
              ref={this.setQuillRef}
              className={quillClassName}
              value={isUncontrolled ? undefined : inputProps.value}
              defaultValue={isUncontrolled ? inputProps.defaultValue : undefined}
              onChange={disabled ? () => {} : inputProps.onChange}
              onFocus={onFocus}
              onBlur={() => {
                if (input.onBlur) input.onBlur();
                if (onBlur) onBlur();
              }}
              readOnly={!!disabled}
              modules={modules}
              formats={formats}
            />
          )}
          <ValidationError fieldMeta={fieldMeta} className={css.errorMessage} />
        </div>
      );
    } else {
      return (
        <div className={classes}>
          {label ? <label htmlFor={id}>{label}</label> : null}
          <input type="hidden" {...inputProps} style={{ display: 'none' }} />
          <textarea />
          <ValidationError fieldMeta={fieldMeta} />
        </div>
      );
    }
  }
}

FieldTextEditorComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  isUncontrolled: false,
  inputRef: null,
  onFocus: null,
  onBlur: null,
};

FieldTextEditorComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,

  onUnmount: func,
  onFocus: func,
  onBlur: func,

  // Error message that can be manually passed to input field,
  // overrides default validation message
  customErrorText: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,

  // Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
  // https://reactjs.org/docs/uncontrolled-components.html#default-values
  isUncontrolled: bool,
  // a ref object passed for input element.
  inputRef: object,

  // Compact toolbar: B / I / U and a bullet list
  isCompact: bool,
  // With isCompact: an H2 button in front of the compact toolbar
  withHeading: bool,
  // Shows a "length/maxLength" counter at the right end of the toolbar row
  maxLength: number,
  // Its error shows only once the editor has lost focus
  minLength: number,
  minLengthMessage: string,

  // Generated by final-form's Field component
  input: shape({
    onChange: func.isRequired,
    // Either 'textarea' or something that is passed to the input element
    type: string,
  }).isRequired,
  meta: object.isRequired,
};

class FieldTextEditor extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    const { disableLinkValidation, isApplicationForm, validate, ...rest } = this.props;
    const {
      isPortfolioWork,
      isBio,
      isExperienceDescription,
      isJobPostingDescription,
      isCompact,
      withHeading,
      maxLength,
      maxLengthMessage,
      minLength,
      minLengthMessage,
    } = this.props;
    // Application form (ProjectProposalPage) intentionally permits links and
    // external info so the freelancer can share references with their proposal.
    const skipLinkValidation = disableLinkValidation || isApplicationForm;
    // The stored HTML must only contain the tags this editor variant offers —
    // Quill strips disallowed formats while editing, but pasted, legacy, or
    // programmatically set content can still contain others.
    const { allowedTags } = getEditorConfig({
      isPortfolioWork,
      isApplicationForm,
      isBio,
      isExperienceDescription,
      isJobPostingDescription,
      isCompact,
      withHeading,
    });
    const validators = [
      allowedHtmlTagsValidation(allowedTags),
      skipLinkValidation ? null : textEditorValidation(),
      typeof maxLength === 'number' ? richTextMaxLength(maxLengthMessage, maxLength) : null,
      validate,
      // After `validate`, so an empty required field reads as required
      typeof minLength === 'number' ? richTextMinLength(minLengthMessage, minLength) : null,
    ].filter(Boolean);
    const combinedValidate =
      validators.length > 1 ? composeValidators(...validators) : validators[0];
    return (
      <Field
        component={FieldTextEditorComponent}
        disableLinkValidation={disableLinkValidation}
        isApplicationForm={isApplicationForm}
        maxLength={maxLength}
        maxLengthMessage={maxLengthMessage}
        {...rest}
        validate={combinedValidate}
      />
    );
  }
}

FieldTextEditor.defaultProps = {
  disableLinkValidation: false,
};

FieldTextEditor.propTypes = {
  // When true, skip the built-in link/contact-info validation from textEditorValidation.
  disableLinkValidation: bool,
  // Optional additional validator composed with the default (runs after the built-in check).
  validate: func,
  // Plain-text character limit; adds a counter and a validator.
  maxLength: number,
  maxLengthMessage: string,
  // Plain-text minimum, not counting surrounding whitespace; adds a validator
  minLength: number,
  minLengthMessage: string,
};

export default FieldTextEditor;
